import { FormattedTime, injectIntl } from 'react-intl';
import Axios from 'axios';
import * as models from '@app/models';
import Injector from '@app/services';
import {
	Grid,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Menu,
	MenuItem,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Switch,
	IconButton,
	Link,
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { USPSAutoManifestScheduleService } from '@app/services/usps-auto-manifest-schedule.service';
import {
	AdminDownloadFileTypeEnum,
	ButtonTypeEnum,
	SnackBarVariants,
	USPSAutoManifestScheduleLastExecutionStatusEnum,
	USPSAutoManifestScheduleStatusEnum,
} from '@app/app.enum';
import Icon from '@app/components/shared/icon.component';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import CustomButton from '@app/components/shared/custom-button.component';
import dayjs from 'dayjs';
import CustomTmePicker from '../shared/custom-time-picker.component';
import { IntlService } from '@app/services/intl.service';
import CustomDialogBox from '@app/components/shared/dialog.component';
import { StyleRules, withStyles } from '@material-ui/core/styles';
import { SharedService } from '@app/services/shared.service';
import Table from '../shared/table.component';
import { Column, UseTableCellProps } from 'react-table';
import PopupState from 'material-ui-popup-state';
import { bindTrigger } from 'material-ui-popup-state';
import { bindMenu } from 'material-ui-popup-state';
import * as Yup from 'yup';
import Ribbon from '../shared/ribbon.component';
import CustomTooltip from '../shared/custom-tooltip.component';
import { AdminManifestService } from '@app/services/admin-manifest.service';
import * as constants from '@app/constants';
import { AccountService } from '@app/services/account.service';

let currentAutoManifestSchedule: models.IGetUSPSAutoManifestScheduleResponseVM = null;
let deleteAutoManifestSchedule: models.IGetUSPSAutoManifestScheduleResponseVM = null;
let statusChangeAutoManifestSchedule: models.IGetUSPSAutoManifestScheduleResponseVM = null;

const styles: StyleRules = {
	card: {
		cursor: 'pointer',
	},
};

const intlService: IntlService = Injector.get(IntlService);
const sharedService: SharedService = Injector.get(SharedService);
const uspsAutoManifestScheduleService: USPSAutoManifestScheduleService = Injector.get(USPSAutoManifestScheduleService);
const adminManifestService: AdminManifestService = Injector.get(AdminManifestService);
const accountService: AccountService = Injector.get(AccountService);

const USPSAutoManifestScheduleComponent: React.FunctionComponent<models.IUSPSAutoManifestScheduleProps> = (props) => {
	const formRef = React.createRef<Formik<FormikValues>>();

	const [isOpenAddEditAutoManifestScheduleDialog, setIsOpenAddEditAutoManifestScheduleDialog] =
		useState<boolean>(false);
	const [isAddAutoManifestSchedule, setIsAddAutoManifestSchedule] = useState<boolean>(false);
	const [isEditAutoManifestSchedule, setIsEditautoManifestSchedule] = useState<boolean>(false);
	const [isOpenDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState<boolean>(false);
	const [isOpenStatusConfirmationDialog, setIsOpenStatusConfirmationDialog] = useState<boolean>(false);
	const [autoManifestScheduleList, setAutoManifestScheduleList] = useState<
		models.IGetUSPSAutoManifestScheduleResponseVM[]
	>([]);

	const initAddAutoManifestSchedule: models.IAutoManifestScheduleVM = {
		time: dayjs().set('s', 0).format(constants.defaultTimePickerFormat), // taking input as HH:mm but saving in db as HH:mm:ss and ss will be 00
		lastExecutedTime: null,
		status: USPSAutoManifestScheduleStatusEnum.Enabled,
		description: '',
		lastExecutionStatus: USPSAutoManifestScheduleLastExecutionStatusEnum.Pending,
	};

	const getTimeFormatInHHmm = (time: string, isSecondNeeded: boolean) => {
		const [hours, minutes] = time.split(':');
		const date = new Date();
		date.setHours(parseInt(hours, 10));
		date.setMinutes(parseInt(minutes, 10));
		if (isSecondNeeded) {
			date.setSeconds(parseInt('00', 10));
		}
		return date;
	};

	const AutoManifestScheduleSchema = Yup.object().shape({
		time: Yup.string().test(
			'Duplicate schedule time',
			intlService.GetFormattedMessage('admin-account-auto-manifest-same-time-error-label'),
			(value: any) => {
				const formattedTime = props.intl.formatTime(getTimeFormatInHHmm(value, true), {
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					hour12: false,
				});

				return (
					autoManifestScheduleList.findIndex(
						(x) =>
							x.time.split(':')[0] === formattedTime.split(':')[0] &&
							x.time.split(':')[1] === formattedTime.split(':')[1]
					) === -1
				);
			}
		),
	});

	useEffect(() => {
		async function GetUSPSAutoManifestScheduleForDefinedCarrierAccount() {
			try {
				const res = await uspsAutoManifestScheduleService.getUSPSAutoManifestSchedule(props.sourceType, props.sourceId);
				const tempList = [];
				tempList.unshift(...((Array.isArray(res) ? res : [res]) as models.IGetUSPSAutoManifestScheduleResponseVM[]));
				setAutoManifestScheduleList(tempList);
			} catch (err) {
				if (!Axios.isCancel(err)) {
					props.openSnackBar(sharedService.parseError(err.data), SnackBarVariants.Error);
				}
			}
		}

		if (props.sourceId) {
			GetUSPSAutoManifestScheduleForDefinedCarrierAccount();
		}
	}, [props.sourceId]);

	const addorEditAutoManifestSchedule = async (values: FormikValues) => {
		const manifestSchedules: models.IAutoManifestScheduleVM[] = [];

		if (autoManifestScheduleList.length > 0) {
			autoManifestScheduleList.forEach((element) => {
				manifestSchedules.push({
					time: element.time,
					status: element.status,
					lastExecutedTime: element.lastExecutedTime,
					description: element.description,
					lastExecutionStatus: element.lastExecutionStatus,
					message: element.message,
					lastGeneratedManifestIds: element.lastGeneratedManifestIds,
				} as models.IAutoManifestScheduleVM);
			});
		}

		if (isAddAutoManifestSchedule) {
			try {
				const newAutoManifestSchedule: models.IAutoManifestScheduleVM = {
					time: values.time,
					lastExecutedTime: null,
					status: values.status,
					description: values.description,
					lastExecutionStatus: USPSAutoManifestScheduleLastExecutionStatusEnum.Pending,
				};

				const addNewAutoManifestSchedule: models.IGetUSPSAutoManifestScheduleResponseVM = {
					sourceId: props.sourceId,
					sourceType: props.sourceType,
					time: newAutoManifestSchedule.time,
					lastExecutedTime: newAutoManifestSchedule.lastExecutedTime,
					status: newAutoManifestSchedule.status,
					description: newAutoManifestSchedule.description,
					lastExecutionStatus: newAutoManifestSchedule.lastExecutionStatus,
				};

				// add the new schedule in the rendering list
				autoManifestScheduleList.push(addNewAutoManifestSchedule);
				setAutoManifestScheduleList(autoManifestScheduleList);

				// add new schedule in upsert auto manifest schedule
				manifestSchedules.push(newAutoManifestSchedule);

				const upsertUSPSAutoManifestScheduleVM: models.IUpsertUSPSAutoManifestScheduleVM = {
					sourceId: props.sourceId,
					sourceType: props.sourceType,
					schedules: manifestSchedules,
				};

				await uspsAutoManifestScheduleService.UpsertUSPSAutoManifestSchedule(upsertUSPSAutoManifestScheduleVM);

				setIsOpenAddEditAutoManifestScheduleDialog(false);
				setIsAddAutoManifestSchedule(false);

				props.openSnackBar(
					props.intl.formatMessage({
						id: 'app.snackbar.carrier-auto-manifest-added-successfully',
					})
				);
			} catch (err) {
				if (!Axios.isCancel(err)) {
					props.openSnackBar(sharedService.parseError(err.data), SnackBarVariants.Error);
				}
			}
		}

		if (isEditAutoManifestSchedule) {
			try {
				const updatedAutoManifestSchedule: models.IAutoManifestScheduleVM = {
					time: values.time,
					lastExecutedTime: currentAutoManifestSchedule.lastExecutedTime,
					status: values.status,
					description: values.description,
					lastExecutionStatus: currentAutoManifestSchedule.lastExecutionStatus,
					message: currentAutoManifestSchedule.message,
					lastGeneratedManifestIds: currentAutoManifestSchedule.lastGeneratedManifestIds,
				};

				// add in the rendering list
				const addNewAutoManifestSchedule: models.IGetUSPSAutoManifestScheduleResponseVM = {
					sourceId: props.sourceId,
					sourceType: props.sourceType,
					time: updatedAutoManifestSchedule.time,
					lastExecutedTime: updatedAutoManifestSchedule.lastExecutedTime,
					status: updatedAutoManifestSchedule.status,
					description: updatedAutoManifestSchedule.description,
					lastExecutionStatus: updatedAutoManifestSchedule.lastExecutionStatus,
					message: updatedAutoManifestSchedule.message,
					lastGeneratedManifestIds: updatedAutoManifestSchedule.lastGeneratedManifestIds,
				};

				// Update render list with new schedule
				let index = autoManifestScheduleList.findIndex((x) => x.time === currentAutoManifestSchedule.time);
				autoManifestScheduleList[index] = addNewAutoManifestSchedule;
				setAutoManifestScheduleList(autoManifestScheduleList);

				// Prepare schedule list for upsert request
				index = manifestSchedules.findIndex((x) => x.time === currentAutoManifestSchedule.time);
				manifestSchedules[index] = updatedAutoManifestSchedule;
				const upsertUSPSAutoManifestScheduleVM: models.IUpsertUSPSAutoManifestScheduleVM = {
					sourceId: props.sourceId,
					sourceType: props.sourceType,
					schedules: manifestSchedules,
				};

				await uspsAutoManifestScheduleService.UpsertUSPSAutoManifestSchedule(upsertUSPSAutoManifestScheduleVM);

				setIsOpenAddEditAutoManifestScheduleDialog(false);
				setIsAddAutoManifestSchedule(false);
				setIsEditautoManifestSchedule(false);

				props.openSnackBar(
					props.intl.formatMessage({
						id: 'app.snackbar.carrier-auto-manifest-updated-successfully',
					})
				);
			} catch (err) {
				if (!Axios.isCancel(err)) {
					props.openSnackBar(sharedService.parseError(err.data), SnackBarVariants.Error);
				}
			}
		}
	};

	const renderAddAutoManifestScheduleDialogBox = () => {
		return (
			<div>
				<Dialog
					open={!!isOpenAddEditAutoManifestScheduleDialog}
					onClose={() => {
						setIsOpenAddEditAutoManifestScheduleDialog(false);
						setIsAddAutoManifestSchedule(false);
						setIsEditautoManifestSchedule(false);
					}}
					fullWidth={true}
					maxWidth='sm'
				>
					<DialogTitle>
						{isAddAutoManifestSchedule
							? intlService.GetFormattedMessage('admin-account-auto-manifest-add-schedule-label')
							: intlService.GetFormattedMessage('admin-account-auto-manifest-edit-schedule-label')}
					</DialogTitle>
					<DialogContent className='pt-2'>
						<Formik
							initialValues={isAddAutoManifestSchedule ? initAddAutoManifestSchedule : currentAutoManifestSchedule}
							ref={formRef}
							onSubmit={async (values) => addorEditAutoManifestSchedule(values)}
							validationSchema={AutoManifestScheduleSchema}
							enableReinitialize={true}
						>
							{(props1: FormikProps<FormikValues>) => (
								<Form noValidate={true}>
									<Grid container={true} spacing={2}>
										<Grid item={true} xs={6}>
											<Field
												name='time'
												label={intlService.GetFormattedMessage('admin-account-auto-manifest-time-column-label')}
												component={CustomTmePicker}
												type='text'
												fullWidth={true}
												variant='outlined'
												value={props1.values?.time ? dayjs(`2022-04-17T${props1.values.time}`) : ''}
												required={true}
												onChange={(e: any) => {
													props1.setFieldValue('time', e ? e.format(constants.timePickerFormat) : '');
												}}
												error={!!props1.errors.time}
												helperText={props1.values?.time?.length ? props1.errors.time : null}
											/>
										</Grid>
										<Grid item={true} xs={6}>
											<TextField
												name='status'
												label={intlService.GetFormattedMessage('admin-account-auto-manifest-status-column-label')}
												select={true}
												fullWidth={true}
												variant='outlined'
												value={
													props1.values?.status ? props1.values?.status : USPSAutoManifestScheduleStatusEnum.Enabled
												}
												SelectProps={{
													renderValue: (value: string) => {
														return props.intl.formatMessage({
															id: `admin-account-auto-manifest-status-${value}-label`,
														});
													},
												}}
												onChange={(e: any) => {
													props1.setFieldValue(
														'status',
														e
															? (e.target.value as USPSAutoManifestScheduleStatusEnum)
															: USPSAutoManifestScheduleStatusEnum.Enabled
													);
												}}
											>
												{Object.entries(USPSAutoManifestScheduleStatusEnum).map(
													([_, value]: [string, string], index: number) => (
														<MenuItem key={index} value={value}>
															{props.intl.formatMessage({
																id: `admin-account-auto-manifest-status-${value}-label`,
															})}
														</MenuItem>
													)
												)}
											</TextField>
										</Grid>
										<Grid item={true} direction='row' xs={12}>
											<Grid item={true} xs={12}>
												<Field
													name='description'
													label={intlService.GetFormattedMessage(
														'admin-account-auto-manifest-description-column-label'
													)}
													component={TextField}
													type='text'
													fullWidth={true}
													variant='outlined'
													value={props1.values?.description ? props1.values.description : ''}
													required={false}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
														props1.handleChange('description')(e);
													}}
												/>
											</Grid>
										</Grid>
									</Grid>
								</Form>
							)}
						</Formik>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => {
								setIsOpenAddEditAutoManifestScheduleDialog(false);
								setIsAddAutoManifestSchedule(false);
								setIsEditautoManifestSchedule(false);
							}}
							variant='outlined'
							color='primary'
						>
							{intlService.GetFormattedMessage('app.generic.cancel-button-label')}
						</Button>
						<CustomButton
							onClick={() => {
								formRef.current.submitForm();
							}}
							buttonText={intlService.GetFormattedMessage('app.generic.save-button-label')}
							type={ButtonTypeEnum.Button}
						/>
					</DialogActions>
				</Dialog>
			</div>
		);
	};

	const renderAddAutoManifestScheduleButtonWithDialog = () => {
		return (
			<React.Fragment>
				<div>
					<Button
						startIcon={<Icon name='plus' />}
						fullWidth={true}
						disabled={false}
						variant='text'
						onClick={() => {
							setIsOpenAddEditAutoManifestScheduleDialog(true);
							setIsAddAutoManifestSchedule(true);
						}}
					>
						{intlService.GetFormattedMessage('admin-account-auto-manifest-add-schedule-label')}
					</Button>
				</div>
				<div>{renderAddAutoManifestScheduleDialogBox()}</div>
				{renderDeleteConfirmationDialog()}
				{renderStatusConfirmationDialog()}
			</React.Fragment>
		);
	};

	const renderDeleteConfirmationDialog = () => {
		return (
			<CustomDialogBox
				open={!!isOpenDeleteConfirmationDialog}
				dialogTitle={intlService.GetFormattedMessage('admin-account-auto-manifest-automanifest-delete-dialog-label')}
				dialogDescription={intlService.GetFormattedMessage(
					'admin-account-auto-manifest-automanifest-delete-dialog-description'
				)}
				closeCustomDialog={(dialogPannel: string, isCancel: boolean) => {
					closeDialog(dialogPannel, isCancel);
				}}
			/>
		);
	};

	const renderStatusConfirmationDialog = () => {
		return (
			<CustomDialogBox
				open={!!isOpenStatusConfirmationDialog}
				dialogTitle={intlService.GetFormattedMessage(
					'admin-account-auto-manifest-automanifest-change-status-dialog-label'
				)}
				dialogDescription={intlService.GetFormattedMessage(
					'admin-account-auto-manifest-automanifest-change-status-dialog-description-label'
				)}
				closeCustomDialog={(dialogPannel: string, isCancel: boolean) => {
					closeDialog(dialogPannel, isCancel);
				}}
			/>
		);
	};

	const closeDialog = async (dialogTitle: string, isCancel: boolean) => {
		if (!isCancel && isOpenDeleteConfirmationDialog) {
			try {
				const manifestSchedules: models.IAutoManifestScheduleVM[] = [];
				const remainingAutoManifestSchedule = autoManifestScheduleList.filter(
					(x) => x.time !== deleteAutoManifestSchedule?.time
				);

				remainingAutoManifestSchedule.forEach((element) => {
					manifestSchedules.push({
						time: element.time,
						status: element.status,
						lastExecutedTime: element.lastExecutedTime,
						lastExecutionStatus: element.lastExecutionStatus,
						lastGeneratedManifestIds: element.lastGeneratedManifestIds,
						message: element.message,
						description: element.description,
					} as models.IAutoManifestScheduleVM);
				});

				setAutoManifestScheduleList(remainingAutoManifestSchedule);

				const upsertUSPSAutoManifestScheduleVM: models.IUpsertUSPSAutoManifestScheduleVM = {
					sourceId: props.sourceId,
					sourceType: props.sourceType,
					schedules: manifestSchedules,
				};
				await uspsAutoManifestScheduleService.UpsertUSPSAutoManifestSchedule(upsertUSPSAutoManifestScheduleVM);

				setOpenDeleteConfirmationDialog(false);
				props.openSnackBar(
					props.intl.formatMessage({
						id: 'app.snackbar.carrier-auto-manifest-deleted-successfully',
					})
				);

				isCancel = true;
			} catch (err) {
				if (!Axios.isCancel(err)) {
					props.openSnackBar(sharedService.parseError(err.data), SnackBarVariants.Error);
				}
			}
		}

		if (!isCancel && isOpenStatusConfirmationDialog) {
			try {
				const manifestSchedules: models.IAutoManifestScheduleVM[] = [];

				autoManifestScheduleList.find((x) => x.time === statusChangeAutoManifestSchedule.time).status =
					statusChangeAutoManifestSchedule.status === USPSAutoManifestScheduleStatusEnum.Enabled
						? USPSAutoManifestScheduleStatusEnum.Disabled
						: USPSAutoManifestScheduleStatusEnum.Enabled;

				autoManifestScheduleList.forEach((element) => {
					manifestSchedules.push({
						time: element.time,
						status: element.status,
						lastExecutedTime: element.lastExecutedTime,
						lastExecutionStatus: element.lastExecutionStatus,
						lastGeneratedManifestIds: element.lastGeneratedManifestIds,
						message: element.message,
						description: element.description,
					} as models.IAutoManifestScheduleVM);
				});

				setAutoManifestScheduleList(autoManifestScheduleList);

				const upsertUSPSAutoManifestScheduleVM: models.IUpsertUSPSAutoManifestScheduleVM = {
					sourceId: props.sourceId,
					sourceType: props.sourceType,
					schedules: manifestSchedules,
				};

				await uspsAutoManifestScheduleService.UpsertUSPSAutoManifestSchedule(upsertUSPSAutoManifestScheduleVM);

				setIsOpenStatusConfirmationDialog(false);
				props.openSnackBar(
					props.intl.formatMessage({
						id: 'app.snackbar.carrier-auto-manifest-status-change-successfully',
					})
				);
				isCancel = true;
			} catch (err) {
				if (!Axios.isCancel(err)) {
					props.openSnackBar(sharedService.parseError(err.data), SnackBarVariants.Error);
				}
			}
		}

		if (isCancel) {
			setIsOpenStatusConfirmationDialog(false);
			setOpenDeleteConfirmationDialog(false);
		}
	};

	const renderAutoScheduleMenuItems = (
		popupState: models.IMenuInjectedProps,
		item: models.IGetUSPSAutoManifestScheduleResponseVM
	) => {
		return (
			<Menu {...bindMenu(popupState)}>
				<MenuItem
					onClick={() => {
						currentAutoManifestSchedule = item;
						setIsOpenAddEditAutoManifestScheduleDialog(true);
						setIsEditautoManifestSchedule(true);
						popupState.close();
					}}
				>
					{intlService.GetFormattedMessage('app.generic.edit-button-label')}
				</MenuItem>
				<MenuItem
					onClick={() => {
						deleteAutoManifestSchedule = item;
						setOpenDeleteConfirmationDialog(true);
						popupState.close();
					}}
					disabled={autoManifestScheduleList.length <= 1 ? true : false}
				>
					{intlService.GetFormattedMessage('app.generic.delete-button-label')}
				</MenuItem>
			</Menu>
		);
	};

	// Colums

	const getGridColumns = (): Array<Column<models.IGetUSPSAutoManifestScheduleResponseVM>> => {
		return [
			{
				accessor: 'time',
				Header: intlService.GetFormattedMessage('admin-account-auto-manifest-time-column-label'),
				width: 150,
				disableSortBy: true,
				Cell: (tableData: UseTableCellProps<models.IGetUSPSAutoManifestScheduleResponseVM>) => {
					return (
						<>
							<FormattedTime
								value={getTimeFormatInHHmm(tableData.row.original.time, false)}
								hour='2-digit'
								minute='2-digit'
								hour12={false}
							/>
						</>
					);
				},
			},
			{
				accessor: 'lastExecutionStatus',
				Header: intlService.GetFormattedMessage('admin-account-auto-manifest-last-executed-date-column-label'),
				width: 200,
				disableSortBy: true,
				Cell: (tableData: UseTableCellProps<models.IGetUSPSAutoManifestScheduleResponseVM>) => {
					let color: models.IRibbonProps['color'] = 'success';

					switch (tableData.row.original.lastExecutionStatus) {
						case USPSAutoManifestScheduleLastExecutionStatusEnum.Pending:
						case USPSAutoManifestScheduleLastExecutionStatusEnum.Skipped:
							color = 'info';
							break;

						case USPSAutoManifestScheduleLastExecutionStatusEnum.Succeeded:
							color = 'success';
							break;

						case USPSAutoManifestScheduleLastExecutionStatusEnum.PartiallySucceeded:
							color = 'warning';
							break;

						case USPSAutoManifestScheduleLastExecutionStatusEnum.Failed:
							color = 'error';
							break;
					}

					return (
						<div className='display-flex items-center'>
							<Ribbon
								text={tableData.row.original.lastExecutionStatus}
								color={color}
								type='rounded'
								classNames='position-relative'
							/>
							{tableData.row.original.message && tableData.row.original.message.length > 0 && (
								<CustomTooltip
									items={[
										{
											value: tableData.row.original.message,
										},
									]}
									applyMinWidth={false}
									className='ml-1'
								>
									<Typography>
										<Icon name='info-circle' />
									</Typography>
								</CustomTooltip>
							)}
						</div>
					);
				},
			},
			{
				accessor: 'lastGeneratedManifestIds',
				Header: intlService.GetFormattedMessage('admin-account-auto-manifest-download-scan-form-column-label'),
				width: 200,
				disableSortBy: true,
				Cell: (tableData: UseTableCellProps<models.IGetUSPSAutoManifestScheduleResponseVM>) =>
					accountService.IsAdmin() &&
					(tableData.row.original.lastExecutionStatus === USPSAutoManifestScheduleLastExecutionStatusEnum.Succeeded ||
						tableData.row.original.lastExecutionStatus ===
							USPSAutoManifestScheduleLastExecutionStatusEnum.PartiallySucceeded) &&
					tableData.row.original.lastGeneratedManifestIds ? (
						<Link
							className='cursor-pointer'
							onClick={() =>
								Promise.all(
									tableData.row.original.lastGeneratedManifestIds.split(',').map((manifestId) => {
										return adminManifestService.getManifestCarrierFile(
											manifestId,
											AdminDownloadFileTypeEnum.ManifestDocument
										);
									})
								)
									.then((links) => links.forEach((link) => window.open(link)))
									.catch(() => {})
							}
						>
							{intlService.GetFormattedMessage('settings.uspsmid.download-button-text')}
						</Link>
					) : (
						'-'
					),
			},
			{
				accessor: 'status',
				Header: intlService.GetFormattedMessage('admin-account-auto-manifest-status-column-label'),
				width: 200,
				disableSortBy: true,
				Cell: (tableData: UseTableCellProps<models.IGetUSPSAutoManifestScheduleResponseVM>) => (
					<Grid item={true} xl={3} lg={3} md={5} sm={5} xs={5}>
						<Switch
							checked={tableData.row.original.status === USPSAutoManifestScheduleStatusEnum.Enabled ? true : false}
							onChange={() => {
								statusChangeAutoManifestSchedule = tableData.row.original;
								setIsOpenStatusConfirmationDialog(true);
							}}
							color='primary'
						/>
					</Grid>
				),
			},
			{
				Header: '',
				id: 'abc',
				width: 100,
				disableSortBy: true,
				Cell: (tableData: UseTableCellProps<models.IGetUSPSAutoManifestScheduleResponseVM>) => (
					<PopupState variant='popper'>
						{(popupState: models.IMenuInjectedProps) => (
							<React.Fragment>
								<IconButton disabled={false} {...bindTrigger(popupState)}>
									<Icon name='ellipsis-vertical' className='icon-dimension-square' />
								</IconButton>
								{renderAutoScheduleMenuItems(popupState, tableData.row.original)}
							</React.Fragment>
						)}
					</PopupState>
				),
			},
		];
	};

	return (
		<Accordion defaultExpanded={props.defaultExpandAccordion}>
			<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
				<Grid container={true} direction='row' justifyContent='space-evenly'>
					<Grid item={true} xs={12}>
						<Typography variant='h6' color='inherit'>
							{intlService.GetFormattedMessage('admin-account-auto-manifest-schedule-accordin-label')}
						</Typography>
					</Grid>
				</Grid>
			</AccordionSummary>
			<AccordionDetails style={{ maxHeight: '186px !important' }} className='flex-column'>
				<Grid container={true} item={true} justifyContent='flex-end' xs={12}>
					{renderAddAutoManifestScheduleButtonWithDialog()}
				</Grid>
				<Grid container={true} item={true} xs={12} spacing={2}>
					<Grid container={true}>
						<Table<models.IGetUSPSAutoManifestScheduleResponseVM>
							columns={getGridColumns()}
							data={autoManifestScheduleList}
							initialState={{ hiddenColumns: !accountService.IsAdmin() ? ['lastGeneratedManifestIds'] : [] }}
						/>
					</Grid>
				</Grid>
			</AccordionDetails>
		</Accordion>
	);
};

export default injectIntl(withStyles(styles)(USPSAutoManifestScheduleComponent));



// WEBPACK FOOTER //
// ./src/components/carriers/usps-auto-manifest-schedule.component.tsx